import { IEvent } from "../../../interfaces/models/IEvent";
import { timestampToDateString, timestampToTimeString } from "../../../utils/dates";
import { countSpecialEventGuestsForProgram } from "../../../utils/specialEventGuests";
import type { SpecialEventGuestsMap } from "../../../utils/specialEventGuests";

export type SpecialEventProgram = NonNullable<IEvent["program"]>[number] & {
  programId: string;
};

export type SpecialEventProgramDisplay = {
  programId: string;
  title: string;
  date: string;
  timeRange: string;
  feeLabel: string;
  guestFeeLabel: string;
};

export type SelectedSpecialEventFees = {
  eventFee: number;
  guestFee: number;
};

function formatUsdAmount (amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function formatFeeLabel (amount: number, currency: string): string {
  return `${formatUsdAmount(amount)} ${currency}`;
}

export function getSpecialEventProgramTitle (
  program: SpecialEventProgram,
  eventTitle?: string,
): string {
  if (program.registrationPageSessionTitle) {
    return program.registrationPageSessionTitle;
  }
  if (eventTitle) {
    return `${eventTitle} - ${program.title}`;
  }
  return program.title;
}

export function buildSpecialEventProgramDisplay (
  program: SpecialEventProgram,
  eventData?: IEvent,
): SpecialEventProgramDisplay {
  const currency = eventData?.fee?.currency || "USD";

  return {
    programId: program.programId,
    title: getSpecialEventProgramTitle(program, eventData?.title),
    date: timestampToDateString(program.startTime),
    timeRange: `${timestampToTimeString(program.startTime)} - ${timestampToTimeString(program.endTime)} hrs`,
    feeLabel: formatFeeLabel(program.fee ?? 0, currency),
    guestFeeLabel: formatFeeLabel(program.specialEventGuestFee ?? 0, currency),
  };
}

export function buildSpecialEventProgramsDisplay (
  programs: SpecialEventProgram[],
  eventData?: IEvent,
): SpecialEventProgramDisplay[] {
  return programs.map((program) =>
    buildSpecialEventProgramDisplay(program, eventData),
  );
}

export function calculateSelectedSpecialEventFees (
  programs: SpecialEventProgram[],
  selectedProgramIds: string[],
  guestsMap: SpecialEventGuestsMap,
): SelectedSpecialEventFees {
  const selected = new Set(selectedProgramIds);
  let eventFee = 0;
  let guestFee = 0;

  for (const program of programs) {
    if (!selected.has(program.programId)) {
      continue;
    }
    eventFee += program.fee ?? 0;
    guestFee +=
      (program.specialEventGuestFee ?? 0) *
      countSpecialEventGuestsForProgram(
        { specialEventGuests: guestsMap },
        program.programId,
      );
  }

  return {
    eventFee: Math.round(eventFee * 100) / 100,
    guestFee: Math.round(guestFee * 100) / 100,
  };
}
